import { useState } from "react";
import { Modal } from "flowbite-react";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../common/components/form/InputFormik";
import TextareaFormik from "../../renters/components/forms/TextareaFormik";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";
import { ASSET_CATEGORY_LABELS, DEPARTMENT_LABELS, IinventoryManagement, TAX_REGIME_LABELS } from "../../../types/inventory-management.type";
import { ASSET_STATUS_LABELS } from "./TableInventoryManagement";
import { createInventoryManagement } from "../../../common/services/Inventory-management.api";
import { useEmployeers } from "../hooks";

export type Props = {
    inventoryManagement?: IinventoryManagement;
    onClose: () => void;
    onSave: (inventoryManagement: IinventoryManagement) => void;
};

const inventoryManagementSchema = Yup.object().shape({
    assetCode: Yup.string().required('Código patrimonial do bem é obrigatório'),
    description: Yup.string().required('Descrição é obrigatória'),
    category: Yup.string().required('Categoria é obrigatória'),
    departmentEnum: Yup.string().required('Departamento é obrigatório'),
    currentStatus: Yup.string().required('Status do Imobilizado é obrigatório'),
    taxRegime: Yup.string().required('Regime Tributário é obrigatório'),
    acquisitionDate: Yup.date().required('Data de aquisição é obrigatória'),
    acquisitionValue: Yup.number()
        .typeError('Valor de Aquisição deve ser um número')
        .required('Valor de Aquisição é obrigatório'),
});


export function ModalCreateInventoryManagement(props: Props) {
    const [, setIsLoading] = useState(false);
    const { notifyError } = useToastContext();
    const initialValues = {
        assetCode: '',
        description: '',
        category: '',
        departmentEnum: '',
        acquisitionDate: '',
        acquisitionValue: '',
        residualValue: '',
        depreciationRate: '',
        netValue: '',
        usefulLifeInMonths: '',
        currentStatus: '',
        fiscalIncentive: false,
        icmsCreditable: false,
        pisCofinsRate: '',
        taxRegime: '',
        invoiceKey: '',
        notes: '',
        employeerId: '',
        serieNumber: ''
    };
    const { data: employeers } = useEmployeers()

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {

            const result = await createInventoryManagement(values)

            if (!result.ok)
                return notifyError(result.message || "Erro ao criar o item de imobilizado");

            props.onSave(result.data);

            props.onClose();
        } catch (error) {
            console.error("Erro ao salvar o agendamento:", error);
            return notifyError("Erro ao criar o item de imobilizado");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            className='relative text-black'
            show={true}
            onClose={() => props.onClose()}
            size="7x"
            theme={{
                content: {
                    base: "relative h-full w-full p-4 md:h-auto",
                    inner: "relative rounded-lg bg-white shadow dark:bg-gray-700 max-w-[90%] max-h-[80vh] mx-auto overflow-hidden flex flex-col"
                }
            }}
        >
            <Modal.Header className='text-black'>
                <h2 className='text-black'>
                    {"Novo item de imobilizado"}
                </h2>
            </Modal.Header>
            <Modal.Body className="min-h-[509px]">
                <Formik
                    initialValues={initialValues}
                    validationSchema={inventoryManagementSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <div className="space-y-4 border rounded-lg p-3">

                                <div className="resumo">
                                    {/* Identificação */}
                                    <div className="mb-6 rounded-lg p-2">
                                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Identificação</h3>

                                        <div className="flex flex-wrap gap-6 mb-4">
                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="Código patrimonial do bem:"
                                                    name="assetCode"
                                                    className="w-full"
                                                    required={true}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="Número de série:"
                                                    name="serieNumber"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <ComboBoxFormik
                                                    label="Categoria:"
                                                    name="category"
                                                    className="w-full"
                                                    required={true}
                                                    options={[
                                                        { value: '', label: 'Selecione uma categoria...' },
                                                        ...Object.entries(ASSET_CATEGORY_LABELS).map(([value, label]) => ({
                                                            value,
                                                            label,
                                                        }))
                                                    ]}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <ComboBoxFormik
                                                    label="Departamento:"
                                                    name="departmentEnum"
                                                    className="w-full"
                                                    required={true}
                                                    options={[
                                                        { value: '', label: 'Selecione um departamento...' },
                                                        ...Object.entries(DEPARTMENT_LABELS).map(([value, label]) => ({
                                                            value,
                                                            label,
                                                        }))
                                                    ]}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <ComboBoxFormik
                                                    label=" Status do Imobilizado:"
                                                    name="currentStatus"
                                                    className="w-full"
                                                    required={true}
                                                    options={[
                                                        { value: '', label: 'Selecione o status...' },
                                                        ...Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => ({
                                                            value,
                                                            label,
                                                        }))
                                                    ]}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <ComboBoxFormik
                                                    label="Colaborador:"
                                                    name="employeerId"
                                                    className="w-full"
                                                    required={false}
                                                    options={[{ value: '', label: 'Selecione o colaborador...' }, 
                                                      ...(employeers?.map((employee) => ({
                                                      value: employee._id ?? "",
                                                      label: employee.name ?? "",
                                                    })) || [])]}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <InputFormik
                                                label="Descrição:"
                                                name="description"
                                                className="w-full"
                                                required={true}
                                                type="textarea"
                                                placeholder="Descreva o bem..."
                                            />
                                        </div>

                                    </div>

                                    {/*  Informações Fiscais e Tributárias */}
                                    <div className="mb-6 rounded-lg p-2" style={{ backgroundColor: "#D9D9D9" }}>
                                        <h3 className="text-lg font-semibold text-gray-700 mb-2"> Informações Fiscais e Tributárias</h3>

                                        <div className="flex flex-wrap gap-4 mb-4">
                                            <div className="flex-1 min-w-[220px]">
                                                <ComboBoxFormik
                                                    label="Regime Tributário:"
                                                    name="taxRegime"
                                                    className="w-full"
                                                    required={true}
                                                    options={[
                                                        { value: '', label: 'Selecione um regime...' },
                                                        ...Object.entries(TAX_REGIME_LABELS).map(([value, label]) => ({
                                                            value,
                                                            label,
                                                        }))
                                                    ]}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px] flex items-center justify-center gap-2">
                                                <label htmlFor="fiscalIncentive" className="font-medium">
                                                    Incentivo Fiscal?
                                                </label>
                                                <input
                                                    id="fiscalIncentive"
                                                    name="fiscalIncentive"
                                                    type="checkbox"
                                                    className="h-5 w-5 accent-green-600 focus:ring-green-500 border-gray-300"
                                                    checked={!!values.fiscalIncentive}
                                                    onChange={e => setFieldValue("fiscalIncentive", e.target.checked)}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px] flex items-center justify-center gap-2">
                                                   <label htmlFor="fiscalIncentive" className="font-medium">
                                                    Gera Crédito de ICMS?
                                                </label>
                                                <input
                                                    id="icmsCreditable"
                                                    name="icmsCreditable"
                                                    type="checkbox"
                                                    className="h-5 w-5 accent-green-600 focus:ring-green-500 border-gray-300"
                                                    checked={!!values.icmsCreditable}
                                                    onChange={e => setFieldValue("icmsCreditable", e.target.checked)}
                                                />
                                            </div>

                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="Alíquota PIS/COFINS (%):"
                                                    name="pisCofinsRate"
                                                    type="number"
                                                    className="w-full"
                                                    placeholder="0,00"
                                                />
                                            </div>

                                        </div>


                                    </div>


                                    {/* Informações Contábeis */}
                                    <div className="mb-6 rounded-lg p-2">
                                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Informações Contábeis</h3>

                                        <div className="flex flex-wrap gap-4 mb-4">

                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="Data de aquisição:"
                                                    name="acquisitionDate"
                                                    type="date"
                                                    className="w-full"
                                                    required={true}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="Valor de Aquisição (R$):"
                                                    name="acquisitionValue"
                                                    type="number"
                                                    className="w-full"
                                                    required={true}
                                                    placeholder="0,00"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="Valor Residual (R$):"
                                                    name="residualValue"
                                                    type="number"
                                                    className="w-full"
                                                    placeholder="0,00"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-4 mb-4">

                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="Depreciação Anual (%):"
                                                    name="depreciationRate"
                                                    type="number"
                                                    className="w-full"
                                                    placeholder="0,00"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="	Vida Útil (meses):"
                                                    name="usefulLifeInMonths"
                                                    type="number"
                                                    className="w-full"
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="	Valor Líquido Atual (R$)"
                                                    name="netValue"
                                                    type="number"
                                                    className="w-full"
                                                    placeholder="0,00"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Nota Fiscal */}
                                    <div className="mb-6 rounded-lg p-2" style={{ backgroundColor: "#D9D9D9" }}>
                                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Nota Fiscal</h3>

                                        <div className="flex flex-wrap gap-4 mb-4">

                                            <div className="flex-1 min-w-[220px]">
                                                <InputFormik
                                                    label="Chave da Nota Fiscal:"
                                                    name="invoiceKey"
                                                    type="text"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>
                                    </div>


                                    <div className="flex flex-wrap mb-4 mt-1">
                                        <div className="w-full">
                                            <label htmlFor="notes" className="block mb-2 text-sm font-medium">
                                                Observações:
                                            </label>
                                            <TextareaFormik
                                                label=""
                                                name="notes"
                                                className="w-full min-h-[150px] rounded shadow-md focus:border-white p-2 border border-gray-300"
                                                placeholder="Descreva informações relevantes..."
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end space-x-3 pt-4">

                                    <ButtonWithLoader
                                        type="submit"
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting}
                                    >
                                        Salvar
                                    </ButtonWithLoader>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    );
}
